import Api from '../api/Api';
import { heFetch } from '../api/HeFetch';
import { handleUnauthorized } from './auth';
import getTrackingPageName from '../api/tracking-page-name.config';
import i18n from '../i18n';

import SavedSearches from '../models/Alert';

export const LOAD_SAVED_SEARCHES = 'LOAD_SAVED_SEARCHES';
export const ADD_SAVED_SEARCH = 'ADD_SAVED_SEARCH';
export const PATCH_SAVED_SEARCH = 'PATCH_SAVED_SEARCH';
export const DELETE_SAVED_SEARCH = 'DELETE_SAVED_SEARCH';

export function loadAlerts(savedSearches) {
    savedSearches = savedSearches.map((savedSearch) => new SavedSearches(savedSearch));
    return {
        type: LOAD_SAVED_SEARCHES,
        payload: savedSearches
    };
}

export function patchAlert(savedSearch) {
    return {
        type: PATCH_SAVED_SEARCH,
        payload: savedSearch
    };
}

export function saveAlert(savedSearch) {
    return {
        type: ADD_SAVED_SEARCH,
        payload: savedSearch
    };
}

export function deleteAlert(savedSearch) {
    return {
        type: DELETE_SAVED_SEARCH,
        payload: savedSearch
    };
}

export function mySavedSearches(orderBy, limit = 1000, offset) {
    return (dispatch) =>
        Api.Alert.me(
            {
                status: [SavedSearches.STATUS_ENABLED, SavedSearches.STATUS_DISABLED]
            },
            orderBy,
            limit,
            offset,
            2
        ).then((alerts) => dispatch(loadAlerts(alerts)));
}

export function updateSavedSearch(search, changes) {
    return (dispatch) =>
        Api.Alert.patch(search, changes).then((response) => {
            const alert = new SavedSearches(response);
            dispatch(patchAlert(alert));
            return alert;
        });
}

export function removeSearch(search) {
    return (dispatch) => Api.Alert.remove(search).then(() => dispatch(deleteAlert(search)));
}

function mapFiltersToData(queryString, filters, isOnboarding) {
    let calendar = null;
    if (filters.calendar?.date_ranges && filters.calendar.date_ranges.length > 0) {
        calendar = {
            dateRanges: filters.calendar.date_ranges.map((range) => ({
                from: range.from,
                to: range.to
            }))
        };

        const flexibleSearch = {};
        if (filters.calendar.flexibility) {
            flexibleSearch.daysFlexibility = filters.calendar.flexibility;
        }
        if (filters.calendar.duration) {
            if (filters.calendar.duration.kind) {
                flexibleSearch.duration = filters.calendar.duration.kind;
            }
            if (filters.calendar.duration.number_of_nights) {
                flexibleSearch.numberOfNights = filters.calendar.duration.number_of_nights;
            }
        }

        if (Object.keys(flexibleSearch).length > 0) {
            calendar.flexibleSearch = flexibleSearch;
        }
    }

    let location = null;
    if (filters.location?.polygon) {
        location = {
            locationId: filters.location.polygon.location_id,
            provider: filters.location.polygon.provider
        };
    } else if (filters.location?.bounds) {
        location = {
            bounds: {
                sw: { lat: filters.location.bounds.sw.lat, lon: filters.location.bounds.sw.lon },
                ne: { lat: filters.location.bounds.ne.lat, lon: filters.location.bounds.ne.lon }
            }
        };
    }

    let guestCriteria = null;
    const guestData = {};

    if (filters.home?.size?.beds) {
        const guests = {};
        if (filters.home.size.beds.adults) guests.adults = filters.home.size.beds.adults;
        if (filters.home.size.beds.children) guests.children = filters.home.size.beds.children;
        if (filters.home.size.beds.babies) guests.babies = filters.home.size.beds.babies;

        if (Object.keys(guests).length > 0) {
            guestData.guests = guests;
        }
    }

    if (filters.guests_nb) {
        guestData.guestCount = parseInt(filters.guests_nb);
    }

    if (Object.keys(guestData).length > 0) {
        guestCriteria = guestData;
    }

    let homeCriteria = null;
    const homeData = {};

    if (filters.home?.type) {
        homeData.homeType = filters.home.type;
    }

    if (filters.home?.hasOwnProperty('is_primary')) {
        homeData.isPrimaryResidence = filters.home.is_primary;
    }

    if (filters.home?.hasOwnProperty('is_private_room')) {
        homeData.availableHomePart = filters.home.is_private_room ? 'shared' : 'entire';
    }

    if (filters.home?.size?.bedrooms || filters.home?.size?.bathrooms) {
        const availableRooms = {};
        if (filters.home.size.bedrooms) availableRooms.bedrooms = parseInt(filters.home.size.bedrooms);
        if (filters.home.size.bathrooms) availableRooms.bathrooms = parseInt(filters.home.size.bathrooms);
        homeData.availableRooms = availableRooms;
    }

    if (filters.guestpoints?.from || filters.guestpoints?.to) {
        const guestPoints = {};
        if (filters.guestpoints.from) guestPoints.min = parseInt(filters.guestpoints.from);
        if (filters.guestpoints.to) guestPoints.max = parseInt(filters.guestpoints.to);
        homeData.guestPoints = guestPoints;
    }

    if (filters.home?.amenities && filters.home.amenities.length > 0) {
        homeData.amenities = filters.home.amenities;
    }
    if (filters.home?.surrounding && filters.home.surrounding.length > 0) {
        homeData.surrounding = filters.home.surrounding;
    }

    if (Object.keys(homeData).length > 0) {
        homeCriteria = homeData;
    }

    let additionalCriteria = null;
    const additionalData = {};

    if (filters.home?.eco_level) {
        additionalData.ecoLevel = parseInt(filters.home.eco_level);
    }
    if (filters.luxury || filters.collection) {
        additionalData.isLuxury = Boolean(filters.luxury || filters.collection);
    }
    if (filters.hasOwnProperty('favorite')) {
        additionalData.inFavorites = Boolean(filters.favorite);
    }

    if (Object.keys(additionalData).length > 0) {
        additionalCriteria = additionalData;
    }

    let animalCriteria = null;
    if (filters.home?.exclude_animals && filters.home.exclude_animals.length > 0) {
        animalCriteria = {
            exclude: filters.home.exclude_animals
        };
    }

    let exchangeTypes = null;
    if (filters.calendar?.exchange_types && filters.calendar.exchange_types.length > 0) {
        exchangeTypes = filters.calendar.exchange_types;
    }

    let filterTags = null;
    if (filters.filters && filters.filters.length > 0) {
        filterTags = filters.filters;
    }

    const searchCriteria = {};

    if (exchangeTypes) searchCriteria.exchangeTypes = exchangeTypes;
    if (calendar) searchCriteria.calendar = calendar;
    if (location) searchCriteria.location = location;
    if (guestCriteria) searchCriteria.guestCriteria = guestCriteria;
    if (animalCriteria) searchCriteria.animalCriteria = animalCriteria;
    if (homeCriteria) searchCriteria.homeCriteria = homeCriteria;
    if (additionalCriteria) searchCriteria.additionalCriteria = additionalCriteria;
    if (filterTags) searchCriteria.filterTags = filterTags;

    const alertConfig = {
        enabled: true,
        locale: i18n.language || 'en'
    };

    const result = {
        alertConfig,
        displayInformation: {
            title: queryString,
            description: null
        },
        source: isOnboarding ? 'onboarding' : 'search',
        searchCriteria
    };

    return result;
}

export function fetchSavedSearches() {
    return (dispatch, getState) => {
        const state = getState();
        const isLoggedIn = Boolean(state.auth.accessToken);

        if (!isLoggedIn) {
            return;
        }

        const baseHeaders = {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language
        };

        const makeRequest = () =>
            heFetch.get('/search/saved-searches?limit=100', {
                headers: {
                    ...baseHeaders,
                    'X-HE-PAGE-NAME': getTrackingPageName(window?.location?.pathname)
                }
            });

        const handleResponse = (response) => {
            const savedSearches = response.data.map((search) => {
                const alertData = {
                    id: search.externalId,
                    externalId: search.externalId,
                    searchCriteria: search.searchCriteria || {},
                    displayInformation: search.displayInformation || {},
                    alertConfig: search.alertConfig || {},
                    queryParams: search.queryParams || '',
                    place: search.displayInformation?.title || '',
                    description: search.displayInformation?.description,
                    status: search.alertConfig?.enabled
                        ? SavedSearches.STATUS_ENABLED
                        : SavedSearches.STATUS_DISABLED,
                    created_at: search.createdAt,
                    userId: state.user?.id,
                    is_onboarding: search.source === 'onboarding'
                };
                return new SavedSearches(alertData);
            });

            dispatch(loadAlerts(savedSearches));
            return savedSearches;
        };

        return handleUnauthorized(dispatch, makeRequest).then(handleResponse);
    };
}

export function addSearch(queryString, filters, isOnboarding = false) {
    return (dispatch, getState) => {
        const isLoggedIn = Boolean(getState().auth.accessToken);

        if (!isLoggedIn) {
            return;
        }

        const data = mapFiltersToData(queryString, filters, isOnboarding);

        const baseHeaders = {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language
        };

        const makeRequest = () =>
            heFetch.post('/search/saved-searches', {
                data: JSON.stringify(data),
                headers: {
                    ...baseHeaders,
                    'X-HE-PAGE-NAME': getTrackingPageName(window?.location?.pathname)
                }
            });

        const handleResponse = (response) => {
            // Store the complete new API structure directly
            const alertResponse = {
                id: response.data.externalId,
                externalId: response.data.externalId,
                // Store the complete new API structure
                searchCriteria: response.data.searchCriteria || {},
                displayInformation: response.data.displayInformation || {},
                alertConfig: response.data.alertConfig || {},
                queryParams: response.data.queryParams || '',
                // Keep legacy fields for backward compatibility during transition
                place: response.data.displayInformation?.title || queryString,
                is_onboarding: isOnboarding,
                created_at: new Date().toISOString(),
                status: SavedSearches.STATUS_ENABLED
            };
            const alert = new SavedSearches(alertResponse);
            dispatch(saveAlert(alert));
            return alert;
        };

        return handleUnauthorized(dispatch, makeRequest).then(handleResponse);
    };
}
